<template>
  <v-row no-gutters>
    <v-col cols="auto" v-if="isFetched">
      <v-row no-gutters class="pb-4">
        <v-col cols="auto">
          <data-input
            :data-type="{
              name: 'select',
            }"
            :items="catalogs"
            :value="selectedItem"
            @change="selectNewItem"
            dense
            outlined
            filled
            return-object
            item-text="text"
          />
        </v-col>
      </v-row>
      <v-row no-gutters class="pb-4">
        <v-col cols="auto">
          <base-button translation-path="button.addToCatalog" @click="openCatalogEditAddDialog(undefined)" />
        </v-col>
        <v-col cols="auto">
          <base-button translation-path="button.editCatalogAttributes" @click="openEditAttributes" cancel />
        </v-col>
        <v-col cols="auto" class="searchBox">
          <v-text-field
            outlined
            dense
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            clearable
            hide-details
            v-model="search"
            class="align-center justify-center"
          />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="10" style="border: 1px solid rgba(0, 0, 0, 0.12); max-width: 70vw">
          <v-data-table
            dense
            :items="computedFeatures"
            :headers="tableHeaders"
            :search="search"
            calculate-widths
            :footer-props="{
              itemsPerPageOptions: [10, 20, 50, 100],
            }"
          >
            <template #item.actions="{item}">
              <dots-menu
                :items="dotsItems"
                @edit="openCatalogEditAddDialog(item)"
                @delete="openDeleteDialog(item)"
              ></dots-menu>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <main-input-dialog
        v-if="isCatalogDialogLoaded"
        :is-visible.sync="isCatalogDialogVisible"
        :inputs="catalogInputs"
        :agree-text="catalogAgreeText"
        dialog-width="40%"
        :title-translation="catalogDialogName"
        @modalClosed="onClose"
        :loading="isConfirmButtonLoading"
        @agree="computedAgreeAction"
      />
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteCatalog')"
        :body="$i18n.t('dialog.body.deleteCatalogConfirmation')"
        @agree="handleCatalogDelete"
        :loading="isConfirmButtonLoading"
        agree-text="dialog.agreeDelete"
        @modalClosed="onClose"
      />
      <data-source-attributes-editing
        v-if="isAttributesEditingLoaded"
        :is-visible.sync="isAttributesEditingVisible"
        :editing-data-source="selectedDatasourceMetadata"
        :is-data-source-from-layer="true"
        @modalClosed="refresh"
      />
    </v-col>
    <v-col v-else>
      <main-loader></main-loader>
    </v-col>
  </v-row>
</template>

<script>
import BaseButton from '@/components/BaseButton';
import DotsMenu from '@/components/DotsMenu';
import { call, get } from 'vuex-pathify';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
export default {
  name: 'ZDMCatalogsTab',
  components: {
    DataSourceAttributesEditing: () => import('@/components/DataSourceAttributesEditing'),
    MainLoader,
    BaseButton,
    DataInput,
    DotsMenu,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    catalogs: {
      type: Array,
      default: () => [],
    },
  },
  data: vm => ({
    isFetched: false,
    search: '',
    selectedItem: vm.catalogs[0] || {},
    dotsItems: [{ name: 'edit' }, { name: 'delete' }],
    isCatalogDialogVisible: false,
    isCatalogDialogLoaded: false,
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isConfirmButtonLoading: false,
    editingCatalog: undefined,
    isAttributesEditingVisible: false,
    isAttributesEditingLoaded: false,
    schema: undefined,
  }),
  computed: {
    dataSourceForm: get('layers/dataSourcesForms@:dataSource'),
    attributesMetadata: get('layers/metadata@:dataSource'),
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    layerMetadata: get('layers/metadata'),
    layer: get('layers/featuresLayers@:layerId'),
    selectedDataSourceFeatures() {
      return this.dataSourcesFeatures[this.dataSource].features;
    },
    selectedDatasourceMetadata() {
      return this.layerMetadata[this.dataSource];
    },
    layerId() {
      return this.item.layerId;
    },
    computedFeatures() {
      return this.selectedDataSourceFeatures.map(value => {
        return {
          id: value.id,
          ...value.properties,
        };
      });
    },

    computedAgreeAction() {
      return this.editingCatalog === undefined ? this.handleCatalogAdd : this.handleCatalogEdit;
    },
    catalogDialogName() {
      return this.editingCatalog === undefined
        ? this.$i18n.t('dialog.title.addCatalog')
        : this.$i18n.t('dialog.title.editCatalog');
    },
    catalogAgreeText() {
      return this.editingCatalog === undefined ? 'dialog.agreeAdd' : 'dialog.agreeSave';
    },
    catalogInputs() {
      return this.schema[0].elements
        .filter(e => e.attribute !== 'id')
        .map(el => {
          return {
            name: el.attribute,
            straightTitle: el.label,
            clearable: true,
            dataType: el.dataType,
            items: el.items,
            value: this.editingCatalog ? this.editingCatalog[el.attribute] : null,
            rules: el.rules,
          };
        });
    },
    tableHeaders() {
      return this.getHeaders(
        this.schema[0].elements.map(e => {
          return {
            text: e.label,
            value: e.attribute,
            align: 'center',
          };
        })
      );
    },
    computedAttributes() {
      if (this.attributesMetadata) {
        return this.attributesMetadata.attributes_schema.attributes;
      }
      return undefined;
    },
    dataSource() {
      return this.catalogs.length > 0 ? this.selectedItem.dataSource : undefined;
    },
  },
  methods: {
    getDataSourceForm: call('layers/getDataSourceForm'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    addDatasourceFeature: call('layers/addDatasourceFeature'),
    editDatasourceFeature: call('layers/editDatasourceFeature'),
    editLayerAction: call('layers/editLayer'),
    getFeaturesLayer: call('layers/getFeaturesLayer'),

    async onClose() {
      this.editingCatalog = undefined;
      this.isConfirmButtonLoading = false;
      await this.getDataSourceFeatures({
        dataSource: this.selectedItem.dataSource,
      });
    },
    async getDatasourceMetadata() {
      if (!this.layerMetadata[this.selectedItem.dataSource]) {
        await this.getLayerMetadata({ dataSource: this.selectedItem.dataSource });
      }
    },
    async getSelectedDatasourceForm() {
      if (!this.dataSourceForm) {
        await this.getDataSourceForm(this.selectedItem.dataSource);
      }
    },
    async getFeatures() {
      if (!this.dataSourcesFeatures[this.selectedItem.dataSource]) {
        await this.getDataSourceFeatures({
          dataSource: this.selectedItem.dataSource,
        });
      }
    },
    openCatalogEditAddDialog(value) {
      if (value) {
        this.editingCatalog = value;
      }
      this.isCatalogDialogLoaded = true;
      this.isCatalogDialogVisible = true;
    },
    openEditAttributes() {
      this.isAttributesEditingLoaded = true;
      this.isAttributesEditingVisible = true;
    },
    openDeleteDialog(value) {
      this.editingCatalog = value;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    getHeaders(values) {
      return [
        ...values,
        {
          text: this.$i18n.t('admin.zdmStreetLights.tableHeaders.options'),
          value: 'actions',
          sortable: false,
          width: '50px',
        },
      ];
    },
    getRules({ required }) {
      const rules = [];
      if (required) {
        rules.push(v => !!v || 'rules.required');
      }

      return rules;
    },

    prepareFormSchema() {
      return this.getAttributes(this.getFormSchema());
    },
    getAttributes(elements) {
      return elements.map(element => {
        if (element.element_type === 'group') {
          return { ...element, elements: this.getAttributes(element.elements) };
        }
        const found = this.computedAttributes.find(attr => attr.name === element.attribute);
        return {
          ...element,
          ...(found && { items: found.allowed_values ? found.allowed_values : [] }),
          ...(found && { unit: found.measurement_unit ? found.symbol : null }),
          ...(found && { dataType: found.dict_id ? { name: 'select' } : found.data_type }),
          rules: this.getRules({ required: element.required }),
        };
      });
    },
    getFormSchema() {
      return this.getAttributesObject(this.dataSourceForm.elements);
    },
    getAttributesObject(elements) {
      if (!elements || elements.length === 0) {
        return [];
      }
      if (elements[0].element_type === 'group') {
        return elements;
      } else {
        return [
          {
            element_type: 'group',
            elements,
            label: this.$i18n.t('admin.attributes'),
          },
        ];
      }
    },

    async handleCatalogDelete() {
      this.isConfirmButtonLoading = true;
      const id = this.editingCatalog.id;
      const datasource = this.selectedItem.dataSource;
      const payload = { datasource, id };
      try {
        await this.deleteDatasourceFeature(payload);
        this.isDeleteDialogVisible = false;
      } finally {
        await this.onClose();
      }
    },
    async handleCatalogAdd() {
      this.isConfirmButtonLoading = true;
      const datasource = this.selectedItem.dataSource;
      const body = this.catalogInputs.reduce((obj, item) => ({ ...obj, [item.name]: item.value }), {});
      const payload = { datasource, body };
      try {
        await this.addDatasourceFeature(payload);
        this.isCatalogDialogVisible = false;
      } finally {
        await this.onClose();
      }
    },
    async handleCatalogEdit() {
      this.isConfirmButtonLoading = true;
      const datasource = this.selectedItem.dataSource;
      const id = this.editingCatalog.id;
      const body = this.catalogInputs.reduce((obj, item) => ({ ...obj, [item.name]: item.value }), {});
      const payload = { datasource, id, body };
      try {
        await this.editDatasourceFeature(payload);
        this.isCatalogDialogVisible = false;
      } finally {
        await this.onClose();
      }
    },
    async selectNewItem(value) {
      this.selectedItem = value;
      this.isFetched = false;
      await Promise.all([this.getDatasourceMetadata(), this.getSelectedDatasourceForm(), this.getFeatures()]);
      this.schema = this.prepareFormSchema();
      this.isFetched = true;
    },

    async init() {
      this.selectedItem = JSON.parse(JSON.stringify(this.catalogs.length > 0 ? this.catalogs[0] : undefined));
      await Promise.all([this.getDatasourceMetadata(), this.getSelectedDatasourceForm(), this.getFeatures()]);
      this.schema = this.prepareFormSchema();
      this.isFetched = true;
    },
    async refresh() {
      await Promise.all([
        this.getLayerMetadata({ dataSource: this.selectedItem.dataSource }),
        this.getDataSourceForm(this.selectedItem.dataSource),
        this.getDataSourceFeatures({
          dataSource: this.selectedItem.dataSource,
        }),
      ]);
      this.schema = this.prepareFormSchema();
    },
  },

  mounted() {
    this.init();
  },
  created() {
    this.$root.$off('attributesChanged');
    this.$root.$on('attributesChanged', this.refresh);
  },
};
</script>

<style scoped lang="scss">
.searchBox {
  padding: 0;
}
::v-deep {
  .v-input__icon .v-icon {
    font-size: 15px;
  }
  .v-text-field__slot input {
    font-size: 12px;
  }
  .v-input__control .v-input__slot {
    min-height: 32px !important;
    max-height: 32px !important;
  }
  .v-input__prepend-inner {
    margin-top: 0 !important;
    align-self: center;
  }
  table > tbody > tr > td:last-child,
  table > thead > tr > th:last-child {
    position: sticky !important;
    position: -webkit-sticky !important;
    right: 0;
    z-index: 2;
    background: white;
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }
  .v-data-table-header th {
    white-space: nowrap;
  }
  .v-list-item__title {
    text-align: left;
  }
  .v-input__slot fieldset {
    border-color: rgba(0, 0, 0, 0.12);
  }
  .v-select__selection {
    max-width: 100%;
  }
  .v-select__selections {
    input {
      display: none !important;
    }
  }
}
</style>
